import React, { useState, useEffect, useContext } from "react";
import useProfile from "common/hooks/useProfile";
import SelectableTable, { Column } from "common/SelectableTable";
import { createPaginatorWithContent } from "common/DataTable";
import styled from "styled-components";
import { classification } from "./common";
import { parsePoliciesIntoSets } from "../api";
import { showDynPolicyRate } from "views/Config/PolicyEdit/utils";

const TableContainer = styled.div`
position: relative;
  width: 100%;
  overflow: auto;
  --is-highest: #3d85c6;
  --is-higher: #6aa84f;
  --is-lower: #e69138;
  --is-lowest: #cc0000;
  --warning: #cc0000;
  .inverted {
    --is-lowest: #3d85c6;
    --is-lower: #6aa84f;
    --is-higher: #e69138;
    --is-highest: #cc0000;
  }
  .dataTable tbody td {
    transition: color 1s, background-color: 1s;
  }
  &.colorize-warnings .dataTable tbody td {
    &.warning {
      color: white;
      background: var(--warning);
    }
  }
  &.colorize-quartiles .dataTable tbody td {
    &.min-q1 {
      color: white;
      background: var(--is-lowest);
    }
    &.q1-median {
      color: white;
      background: var(--is-lower);
    }
    &.median-q3 {
      color: white;
      background: var(--is-higher);
    }
    &.q3-max {
      color: white;
      background: var(--is-highest);
    }
  }
`;

const PAGE = 0;
const NUMLINES = 50;
const RETURN_TO = "viewStatusSubscribers";

const getGroups = () => [
  {
    field: "MAX-Mbps",
    label: "MAX-Mbps",
    units: "Mbps",
    legend: "Max speed last 24h",
    tooltip:
      "maximum speed in Mbps during the previous 24 hours or since last subs-id/policy change",
    percentage: true,
  },
  {
    field: "RTT-ms",
    label: "RTT-ms",
    inverted: true,
    units: "ms",
    legend: login.isPlatform("cambium")
      ? "Latency from QoE"
      : "Latency from bqn",
    tooltip:
      "Moving average of latency, in milliseconds. If downlink, latency from this node to subs, if uplink, latency from this node to Internet server",
  },
  {
    field: "RTX",
    label: "RTX",
    inverted: true,
    units: "% retransmisions",
    legend: "TCP packet retransmission",
    tooltip:
      "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",
    percentage: true,
  },
  {
    field: "MAX-SPEED",
    label: "MAX-SPEED-%",
    inverted: true,
    units: "% of traffic",
    legend: "% traffic sent at max speed",
    tooltip:
      "Moving average of the percentage of traffic sent with a speed close to the maximum",
    percentage: true,
  },
  {
    field: "CONGEST",
    label: "CONGESTION",
    tooltip: "Moving average of percentage of traffic suffering congestion",
    inverted: true,
    units: "% of traffic",
    legend: "% traffic under congestion",
    percentage: true,
  },
];

const getPolicyType = (types, sources, name) => {
    if (sources.static.has(name) !== true) {
      return "dynamic";
    }
    if (types.rate.has(name)) {
      return "rate";
    }
    if (types.flow.has(name)) {
      return "flow";
    }
    return "monitor";
  };

export const openPolicy = (name, timePeriod) =>
    ifCl
      .run("show policy")
      .then(parsePoliciesIntoSets)
      .then(({ types, sources }) => {
        const policyType = getPolicyType(types, sources, name);
        switch (policyType) {
          case "dynamic":
            showDynPolicyRate(name);
            break;
          case "rate":
            globalNavigate("viewPolicyRateEdit", {
              returnView: RETURN_TO,
              timePeriod,
              policy: name,
            });
            break;
          case "flow":
            globalNavigate("viewPolicyFlowEdit", {
              returnView: RETURN_TO,
              timePeriod,
              policy: name,
            });
            break;
          case "monitor":
            globalNavigate("viewPolicyMonitorEdit", {
              returnView: RETURN_TO,
              timePeriod,
              policy: name,
            });
            break;
          default:
        }
      });
  
  const openPolicyOrReport = (...args) =>
    openPolicy(...args).catch((error) => {
      const notFound = String(error).match(/%ERR-ENOENT: Cannot find "(.*)" policy/);
      if (notFound) {
        const [, policyName] = notFound;
        const message = `Assigned policy ${policyName} is not defined. A policy with this name needs to be configured, or the subscriber will be assigned a configured policy according to the existing rules.`;
        showModalError("Error:", message);
      } else {
        showModalError("Error:", error);
      }
    });
  
  const openSubscriberDetails = (target, timePeriod) => {
    views.doKeep(RETURN_TO);
  
    globalNavigate("viewStatusSubscribers", {
      ...target,
      returnView: RETURN_TO,
      timePeriod,
    });
  };

export function getColumns(headerIndexMap) {
  let timePeriod = "oneDay";
  location.search
    .slice(1)
    .split("&")
    .forEach((searchParam) => {
      const [key, value] = searchParam.split("=");
      if (key === "timePeriod") {
        timePeriod = value;
      }
    });
  const columns = [
    Column.IPAddress({
      label: "IP-ADDR",
      idx: headerIndexMap["ADDR"],
      cellClassName: "hyperlink-text",
      title: "Subscriber’s IP address",
      onClick: (addr) => openSubscriberDetails({ addr }, timePeriod),
    }),
    Column.Text({
      label: "SUBS-ID",
      idx: headerIndexMap["SUBSCRIBER-ID"],
      cellClassName: "hyperlink-text",
      title: "Subscriber ID",
      onClick: (subsId) => openSubscriberDetails({ subsId }, timePeriod),
    }),
    Column.Text({
      label: "BLOCK",
      title: login.isPlatform("gs-kr")
        ? "Blocking from quota"
        : "Blocking from billing configuration or quota",
      idx: headerIndexMap["BLOCK"],
      colClassName: "align-left",
    }),
    Column.Text({
      label: "RATE-POLICY",
      title: "Name of the subscriber's rate police",
      idx: headerIndexMap["POLICY-RATE"],
      cellClassName: "hyperlink-text",
      colClassName: "align-left",
      onClick: (policy) => openPolicyOrReport(policy, timePeriod),
    }),
    Column.NumberOrNA({
      label: "MBYTES",
      title:
        "Total up or down traffic volume of this subscriber since it became active (see active lifetime field)",
      idx: headerIndexMap["MBYTES"],
      precision: 0,
      colClassName: "align-right",
    }),
    Column.Number({
      label: "FLOWS",
      title:
        "Total number of currently active traffic flows (TCP connections, UDP or IP flows)",
      idx: headerIndexMap["FL-ACTIVE"],
      precision: 0,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "CURR-Mbps",
      title: "Current speed in Mbps",
      idx: headerIndexMap["CURR-Mbps"],
      precision: 1,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "MEAN-Mbps",
      title:
        "Moving average of the mean speed (calculated every 10 minutes) in Mbps.",
      idx: headerIndexMap["MEAN-Mbps"],
      precision: 1,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "MAX-Mbps",
      title: "maximum speed in Mbps during the previous 24 hours",
      idx: headerIndexMap["MAX-Mbps"],
      precision: 1,
      thousands: false,
      colClassName: "align-right",
      cellClassName: maxMbpsCellClassName,
    }),
    Column.HiddenNumberOrNA({
      label: "LIM-Mbps",
      idx: headerIndexMap["LIM-Mbps"],
      precision: 1,
    }),
    Column.NumberOrNA({
      label: "RTT-ms",
      title:
        "Moving average of latency, in milliseconds. If downlink, latency from this node to subs, if uplink, latency from this node to Internet server",
      idx: headerIndexMap["RTT-ms"],
      precision: 1,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "RTX",
      title:
        "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",

      idx: headerIndexMap["RTX"],
      percent: true,
      precision: 1,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "MAX-SPEED-%",
      field: "MAX-SPEED",
      title:
        "Moving average of the percentage of traffic sent with a speed close to the maximum",
      idx: headerIndexMap["MAX-SPEED"],
      percent: true,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "CONGESTION",
      field: "CONGEST",
      title: "Moving average of percentage of traffic suffering congestion",
      idx: headerIndexMap["CONGEST"],
      percent: true,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "WARN",
      title: "Number of warnings",
      idx: headerIndexMap["WARN"],
      percent: false,
      colClassName: "align-right",
    }),
    Column.Time({
      label: "LIFETIME",
      title: "Duration so far of this subscriber session",
      idx: headerIndexMap["LIFETIME"],
      colClassName: "align-right",
    }),
  ];
  return columns;
}

const maxMbpsCellClassName = (
  _value,
  [
    _addr,
    _lifetime,
    _block,
    _policy,
    _total,
    _flows,
    _cur,
    mean,
    value,
    limit,
    ..._rest
  ],
  column
) =>
  limit === null
    ? "un-limited"
    : column.threshold === undefined || column.threshold === null
    ? "no-threshold"
    : column.threshold.calculated === true
    ? "automatic-threshold"
    : value === null
    ? "n-a"
    : column.threshold.inWarning((value / limit) * 100, mean) +
      ` limit-${limit}`;

const extendWithQuantileAndClassication = (
  { field, inverted, threshold, meaningful, ...rest },
  paginator
) => ({
  field,
  inverted,
  ...classification(
    paginator.getColumnValues(field),
    inverted,
    threshold,
    meaningful
  ),
  ...rest,
});

const filterOutWarning = (classify) =>
  typeof classify === "function"
    ? (...args) => classify(...args).replace(/warning/, "")
    : classify;

const provideValueAndMeanTo =
  (classify) =>
  (
    value,
    [_addr, _subsId, _block, _policy, _total, _active, _curMbps, mean, ...rest],
    _column
  ) =>
    classify(value, mean);

const extendColumnWithContext = (
  { label, cellClassName, ...column },
  { threshold = {}, classify, ...context }
) => ({
  threshold,
  ...context,
  cellClassName:
    label === "MAX-Mbps"
      ? mergeCellClassNames(
          cellClassName,
          filterOutWarning(provideValueAndMeanTo(classify))
        )
      : mergeCellClassNames(cellClassName, provideValueAndMeanTo(classify)),
});

const addQuartileClassification = (statistics, columns) => {
  const { groups } = statistics;
  const contextByField = Object.fromEntries(
    groups.map(({ field, classify, quartiles, threshold }) => [
      field,
      { classify, quartiles, threshold },
    ])
  );
  return columns.map((column) => ({
    ...column,
    ...(column.field in contextByField
      ? extendColumnWithContext(column, contextByField[column.field])
      : {}),
  }));
};

const doesNothing = () => {};

const applyProfileRestrictions = ({ hideRates = false }, columns) => {
  return hideRates !== true
    ? columns
    : columns.map(({ label, ...column }) =>
        label === "RATE-POLICY"
          ? {
              label,
              ...column,
              cellClassName: null,
              onClick: null,
            }
          : {
              label,
              ...column,
            }
      );
};

const mergeCellClassNames = (cellClassName, classify) =>
  cellClassName === undefined && classify === undefined
    ? undefined
    : cellClassName === undefined
    ? (...args) => `not-merged ${classify(...args)}`
    : typeof cellClassName === "function"
    ? (...args) =>
        `merged-classes ${cellClassName(...args)} ${classify(...args)}`
    : `merged-classes ${cellClassName}`;

const ListSubscribers = ({ tableData, colorize, thresholds, onPlotSelection = doesNothing }) => {
  const profile = useProfile();
  const groups = getGroups();
  const groupsWithThresholds = groups.map(({ field, ...group }) =>
    thresholds[field] === undefined
      ? { field, ...group }
      : {
          field,
          threshold: thresholds[field],
          meaningful: thresholds["MEAN-Mbps"],
          ...group,
        }
  );

  if (!profile || !tableData) {
    return null;
  }

  const [header] = tableData.trim(/\s/).split("\n");
  const headerAttrs = header.trim().split(/\s+/);
  const headerIndexMap = headerAttrs.reduce((acc, value, index) => {
    acc[value] = index;
    return acc;
  }, {});
  const columns = getColumns(headerIndexMap);
  const paginator = createPaginatorWithContent(tableData, columns);
  const total = paginator.getTotal();
  const statistics = {
    total,
    groups: groupsWithThresholds.map((group) =>
      extendWithQuantileAndClassication(group, paginator)
    ),
  };
  const columnsWithRestrictions = applyProfileRestrictions(
    profile,
    statistics === undefined
      ? paginator.getColumns()
      : addQuartileClassification(statistics, paginator.getColumns())
  );

  const asObjects = ([addr, subsId]) => ({ addr, subsId });

  return (
    <TableContainer
      className={colorize ? "colorize-quartiles" : "colorize-warnings"}
      id="subscribersTable"
    >
      <SelectableTable
        selectionField={"plot"}
        selectionLabel={"PLOT"}
        asObject={asObjects}
        onSelection={onPlotSelection}
        selectFirst={25}
        pageLength={NUMLINES}
        page={PAGE}
        exportAsCSV={true}
        paginator={paginator}
        columns={columnsWithRestrictions}
      />
    </TableContainer>
  );
};

export default ListSubscribers;